import TeamTable from "./TeamTable";
import InviteMembersDialog from "./InviteMembersDialog";
import { TeamPermissions } from "@repo/api/team-roles";
import AppHeader from "@/lib/components/AppHeader";
import { TeamSwitcher } from "@/lib/components/TeamSwitcher";
import UpdateTeamDialog from "./UpdateTeamDialog";
import { useCurrentTeamMembership } from "@/lib/hooks/team-hooks";
import { Form } from "@/lib/components/Form";
import { z } from "zod";
import { useForm, useFieldArray } from "react-hook-form"; // Import useFieldArray
import { CreateInviteDto } from "@repo/api/invite";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTeamMembersQuery } from "@/lib/queries/team-queries";
import { TeamMembersDto } from "@repo/api/team";
import { useNavigate } from "react-router";
import { paths } from "@/App";
import NotificationsDropdown from "@/lib/components/NotificationsDropdown";

export const createInviteSchema = (existingEmails: string[] = []) =>
  CreateInviteDto.omit({ email: true }).extend({
    emails: z
      .array(
        z.object({
          value: z
            .string()
            .min(1, {
              message: "Email is required",
            })
            .refine((email) => !existingEmails.includes(email), {
              message: "Email already exists",
            }),
        }),
      )
      .min(1, {
        message: "At least one email is required",
      }),
  });

export function getExistingEmails(teamMembers: TeamMembersDto) {
  const invitedEmails = teamMembers.invites.map((invite) => invite.email);
  const activeEmails = teamMembers.members.map((member) => member.contactEmail);

  return invitedEmails.concat(activeEmails);
}

function Teams() {
  const navigate = useNavigate();
  const teamMembership = useCurrentTeamMembership();
  const teamExternalId = teamMembership?.teamExternalId;
  const { data: teamMembers } = useTeamMembersQuery(teamExternalId!);

  const existingEmails = teamMembers ? getExistingEmails(teamMembers) : [];
  const InviteSchema = createInviteSchema(existingEmails);

  const form = useForm<z.infer<typeof InviteSchema>>({
    resolver: zodResolver(InviteSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      emails: [{ value: "" }],
      role: "editor",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "emails",
  });

  if (!teamMembership || !teamExternalId) {
    return null;
  }

  const userIsAllowedToEdit = teamMembership.permissions.includes(
    TeamPermissions.TEAM_EDIT,
  );

  return (
    <Form {...form}>
      <AppHeader
        className="border-b border-border"
        actionChildrenClassName="max-md:border-t max-md:border-border"
        actionChildren={
          <div className="flex items-center gap-4">
            <NotificationsDropdown />
            {userIsAllowedToEdit && (
              <>
                <InviteMembersDialog
                  fields={fields}
                  append={append}
                  remove={remove}
                />
                <UpdateTeamDialog />
              </>
            )}
          </div>
        }
      >
        <TeamSwitcher
          classNameTrigger="py-2"
          isUserVisible={false}
          teamExternalId={teamExternalId}
          onTeamSwitch={(newTeamExternalId) => {
            navigate(paths.team(newTeamExternalId));
          }}
        />
      </AppHeader>
      <TeamTable fields={fields} append={append} remove={remove} />
    </Form>
  );
}

export default Teams;
